import React, { useMemo } from "react";
import {
  AccidentItemHSPProps,
  AccidentList,
  CardSmallStats,
  PhotoGalleryProps,
  PhotoGalleryItem,
  AccidentType,
} from "@/components";
import { NoDamageHistoryCard } from "@/components/NoDamageHistoryCard";
import { EntryV2, Photos, ReportStatsV2 } from "@/api/mappers/hsp/api/report";

export interface HSPHistoryData {
  accidents?: AccidentItemHSPProps[];
}

export interface HSPHistoryProps {
  historyData: EntryV2[];
  photos?: Photos;
  reportStats?: ReportStatsV2;
  /**
   * Pojazd został sprawdzony i nie odnotowano dla niego szkód
   * (dodatek HSP_VEHICLE_HISTORY o statusie NOT_FOUND). Zamiast statystyk
   * i listy szkód renderujemy kartę "Nie wykazano historii szkodowej".
   */
  noDamage?: boolean;
}

const mapHistoryDataToAccidents = (
  historyData: EntryV2[],
  photos?: Photos
): AccidentItemHSPProps[] => {
  return historyData.map((entry) => {
    // Znajdź zdjęcia dla tego claimId
    let photoGallery: PhotoGalleryProps | undefined;

    if (photos?.claims && entry.claimId && photos.claims[entry.claimId]) {
      const photoPaths = photos.claims[entry.claimId];
      const photoItems: PhotoGalleryItem[] = photoPaths
        .filter((photoPath): photoPath is string => Boolean(photoPath))
        .map((photoPath, index) => ({
          src: `${photos.baseUrl}${photoPath}`,
          alt: `Zdjęcie szkody ${entry.claimId} - ${index + 1}`,
          title: `Szkoda z dnia ${entry.displayDate}`,
          width: 800,
          height: 600,
        }));

      if (photoItems.length > 0) {
        photoGallery = {
          photos: photoItems,
          galleryVariant: "damage-hsp",
        };
      }
    }

    return {
      id: entry.id,
      date: entry.displayDate,
      damageValueMin: parseInt(entry.damageValueRange.split(" - ")[0]),
      damageValueMax: parseInt(entry.damageValueRange.split(" - ")[1]),
      damageCountry: entry.countryName,
      damageGroups: entry.damageGroups,
      currency: entry.currencyName,
      mileage: entry.mileage.toString(),
      positionsIds: entry.damages.positionsIds?.filter((id) => id !== "00"),
      damageType: AccidentType.ACCIDENT,
      description: entry.damages.description,
      damageLocation:
        entry.damagesNames.length > 0
          ? entry.damagesNames
          : ["Strefa niezdefiniowana"],
      photoGallery,
      flagUrl: `/images-raport/flags/${entry.countryCode}.png`,
    };
  });
};

export const HSPHistory: React.FC<HSPHistoryProps> = ({
  historyData,
  photos,
  reportStats,
  noDamage,
}) => {
  const mappedHistoryData = useMemo(
    () => mapHistoryDataToAccidents(historyData, photos),
    [historyData, photos]
  );

  // Użyj totalDamage z reportStats jeśli dostępne, w przeciwnym razie oblicz lokalnie
  const totalDamageFormatted = useMemo(() => {
    if (reportStats?.totalDamage && reportStats.totalDamage.length > 0) {
      return reportStats.totalDamage
        .map((damage) => `${damage.value} ${damage.currencyName}`)
        .join(", ");
    }
    // Fallback do lokalnego obliczenia
    // const localTotal = mappedHistoryData.reduce(
    //   (acc, curr) => acc + curr.damageValueMax!,
    //   0
    // );
    // const currency = mappedHistoryData[0]?.currency || "";
    // return `${localTotal} ${currency}`;
  }, [reportStats, mappedHistoryData]);

  const totalDamageCount = useMemo(
    () => reportStats?.numberOfEntries ?? mappedHistoryData.length,
    [reportStats, mappedHistoryData]
  );

  const countries = useMemo(() => {
    // utowrzyć set z krajami
    return new Set(mappedHistoryData.map((el) => el.damageCountry));
  }, [mappedHistoryData]);
  if (noDamage) {
    return (
      <div className="mx-auto max-w-[620px]  lg:max-w-[700px] py-[80px]">
        <NoDamageHistoryCard />
      </div>
    );
  }

  const accidents = mapHistoryDataToAccidents(historyData, photos);
  return (
    <div className="mt-6 space-y-8 lg:mt-10">
      {/* Statystyki historii */}
      {historyData.length > 0 && (
        <div className="grid grid-cols-1 gap-6 mb-20 md:grid-cols-2 xl:grid-cols-4">
          <CardSmallStats
            icon="car-accident"
            title="Liczba odnotowanych szkód"
            subtitle={`ilość szkód: ${totalDamageCount}`}
            variant="warning"
          />

          <CardSmallStats
            icon="ruler-measure"
            title="Łączna wartość napraw"
            subtitle={totalDamageFormatted}
          />

          <CardSmallStats
            icon="car-crash"
            variant="danger"
            title="Szkody całkowite"
            subtitle={`ilość szkód: ${0}`}
          />

          <CardSmallStats
            icon="location-pin"
            title="Kraje występowania szkód"
            subtitle={`${Array.from(countries).join(", ")}`}
          />
        </div>
      )}
      {/* Historia szkód */}
      {historyData.length > 0 && (
        <AccidentList
          accidents={accidents}
          title="Historia szkód pojazdu"
          showCount={historyData.length > 0}
          i18n={{
            title: "Historia szkód pojazdu",
            noAccidentsTitle: "Brak zarejestrowanych szkód",
            noAccidentsDescription: "Nie znaleziono żadnych zapisów o szkodach w historii tego pojazdu.",
            entryNumberPrefix: "Wpis nr",
            damageReportedTitle: "Odnotowano szkodę pojazdu",
            damageValueLabel: "Wartość szkody",
            damageCountryLabel: "Kraj szkody",
            eventDetailsTitle: "Szczegóły zdarzenia:",
            damagedElementsLabel: "Elementy które mogły ulec uszkodzeniu:",
            damageLocationLabel: "Miejsce szkody:",
            mileageLabel: "Przebieg przy wystąpieniu zdarzenia",
            kmUnit: "km",
            photoGalleryTitle: "Dokumentacja fotograficzna odnotowanej szkody",
            flagAltTemplate: "Flaga {country}",
            closeGalleryText: "Zamknij galerię",
            openGalleryText: "Zobacz galerię",
          }}
        />
      )}
    </div>
  );
};
